import React from 'react'
import { IntlShape } from 'react-intl'

import { NpAppointmentType } from '@lyrahealth-inc/shared-app-logic/src/models/calendar/CalendarV2'
import { CalendarOutlineIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/CalendarOutlineIcon'
import { TaskListItemProps } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/taskListItem/TaskListItem'
import { BodyTextSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

const TESTING_SESSION_NUMBERS: Partial<Record<NpAppointmentType, string>> = {
  [NpAppointmentType.NP_TESTING_SESSION_1]: '1',
  [NpAppointmentType.NP_TESTING_SESSION_2]: '2',
}

/** One "Schedule" task that names every session still to book, e.g. "Intake, testing session 1 and 2". */
export const mapNpSessionsToScheduleListItem = ({
  sessionTypes,
  intl,
}: {
  sessionTypes: NpAppointmentType[]
  intl: IntlShape
}): TaskListItemProps | undefined => {
  const hasIntake = sessionTypes.includes(NpAppointmentType.NP_CLINICAL_INTAKE)
  const testingNumbers = sessionTypes.flatMap((type) => TESTING_SESSION_NUMBERS[type] ?? [])
  if (!hasIntake && testingNumbers.length === 0) {
    return undefined
  }

  const parts: string[] = []
  if (hasIntake) {
    parts.push(intl.formatMessage({ defaultMessage: 'Intake', description: 'NP session to schedule, in a task title' }))
  }
  if (testingNumbers.length > 0) {
    const sessions = intl.formatList(testingNumbers)
    parts.push(
      hasIntake
        ? intl.formatMessage(
            {
              defaultMessage: 'testing session {sessions}',
              description: 'NP testing sessions to schedule, following the intake in a task title',
            },
            { sessions },
          )
        : intl.formatMessage(
            {
              defaultMessage: 'Testing session {sessions}',
              description: 'NP testing sessions to schedule, at the start of a task title',
            },
            { sessions },
          ),
    )
  }

  return {
    icon: <CalendarOutlineIcon size={24} />,
    taskType: intl.formatMessage({ defaultMessage: 'Schedule', description: 'Task type label' }),
    title: intl.formatList(parts, { type: 'unit', style: 'long' }),
    taskTypeSize: BodyTextSize.SMALL,
    testId: tID('TasksModule-schedule-np-sessions'),
  }
}
